import { useEffect, useRef, useState } from 'react';
import authFetch from '../utils/authFetch';

const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;

// Busca de pessoas no servidor (GET /users/search — sem acento, paginada).
// Extraido do InviteMembersModal original (M1a) pra ser reaproveitado tambem
// no dialogo de criacao de area de trabalho. excludeIds: Set opcional de
// user ids a esconder dos resultados (ja convidados/ja escolhidos).
export default function useUserSearch({ excludeBoardId = null, excludeIds = null } = {}) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const seqRef = useRef(0); // descarta resposta atrasada de uma busca superada
  const debounceRef = useRef(null);

  const runSearch = async (q, offset, append) => {
    const mySeq = ++seqRef.current;
    setSearching(true);
    setSearchError(null);
    try {
      const params = new URLSearchParams({ q, limit: String(SEARCH_LIMIT), offset: String(offset) });
      if (excludeBoardId) params.set('exclude_board_id', String(excludeBoardId));
      const data = await authFetch(`/api/v1/users/search?${params.toString()}`);
      if (mySeq !== seqRef.current) return; // resposta de uma busca ja superada
      setResults((prev) => (append ? [...prev, ...data.items] : data.items));
      setHasMore(!!data.has_more);
    } catch (err) {
      if (mySeq !== seqRef.current) return;
      setSearchError(err.message || 'Falha na busca');
    } finally {
      if (mySeq === seqRef.current) setSearching(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = search.trim();
    if (!q) {
      setResults([]);
      setHasMore(false);
      setSearchError(null);
      setSearching(false);
      return;
    }
    debounceRef.current = setTimeout(() => runSearch(q, 0, false), SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, excludeBoardId]);

  const handleShowMore = () => {
    const q = search.trim();
    if (!q || searching) return;
    runSearch(q, results.length, true);
  };

  const handleRetrySearch = () => {
    const q = search.trim();
    if (!q) return;
    runSearch(q, 0, false);
  };

  const reset = () => {
    seqRef.current++;
    setSearch('');
    setResults([]);
    setHasMore(false);
    setSearchError(null);
    setSearching(false);
  };

  const visibleResults = excludeIds ? results.filter((u) => !excludeIds.has(u.id)) : results;

  return {
    search,
    setSearch,
    results: visibleResults,
    hasMore,
    searching,
    searchError,
    handleShowMore,
    handleRetrySearch,
    reset,
  };
}
